import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "children", "verticalPositioning", "offsetOutline"];
import styled from 'foundations-theming/utils';
import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { UPDATE_COLLABORATION_SIDEBAR_STATE } from 'ContentEditorUI/redux/actions/actionTypes';
import { getCollaborationSidebarState } from 'ContentEditorUI/redux/selectors/collaborationSidebarSelectors';
import { getIsEmailTemplate } from 'EmailEditorUI/redux/selectors/emailCategoriesSelectors';
import ControlledCommentableArea from 'collaboration-sidebar/features/deepCommenting/components/ControlledCommentableArea';
import { NAMESPACE_ENABLE } from 'EmailEditorUI/components/NamespaceWrapper';
import { getAuth } from 'ContentEditorUI/redux/selectors/authSelectors';

// @ts-expect-error File is not typed
import MouseEventsHandler from 'LayoutBuilderUI/components/utility/MouseEventsHandler';
import { COMMENTABLE_AREA } from 'LayoutBuilderUI/lib/DragDropConstants';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { EARTH_LAYER } from 'foundations-components/transitional/constants';
import { keyframes } from 'styled-components';
import { CommentsIcon } from 'foundations-assets/icons/components/CommentsIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const BADGE_SIZE = 27;
const fadeIn = keyframes(["from{opacity:0;}to{opacity:1;}"]);
// Hoists the index of the hovered / selected hoverable area to ensure the outline is shown above when in comment mode.
// We also hide the drag-preview so it's not visible. This is ok as moving elements is not possible in comment mode
const StyledControlledCommentableArea = styled(ControlledCommentableArea).withConfig({
  displayName: "CommentableArea__StyledControlledCommentableArea"
})([".hs-private.hs-private.hs-private &{", " ", "}"], ({
  offsetOutline
}) => offsetOutline && `outline-offset: -2px;`, ({
  isCommentModeActive
}) => isCommentModeActive && `
      &:hover, &.commenting-commentable-area {
        z-index: 1;
      }
    `);
const HoverableIconCircle = styled.span.withConfig({
  displayName: "CommentableArea__HoverableIconCircle"
})([".hs-private.hs-private.hs-private &{position:relative;display:flex;border-radius:50%;z-index:", ";opacity:0;animation:0.1s ", " 0s 1 ease-out forwards;transition:box-shadow ease-out 150ms;pointer-events:all;box-sizing:border-box;font-size:", ";}.hs-private.hs-private.hs-private ", " &:hover{cursor:pointer;", "}.hs-private.hs-private.hs-private & *{box-sizing:border-box !important;}"], EARTH_LAYER, fadeIn, ({
  theme
}) => theme.typography['body-100'].fontSize, ({
  borderColor,
  theme
}) => borderColor && `border-color: ${theme.color[borderColor] || borderColor};`, ({
  boxShadow,
  theme
}) => boxShadow && `box-shadow: 0 0 0 1px ${theme.color['border-interactive-pressed']}, 0 0 8px 0 ${theme.color['border-interactive-pressed']};`);
const NotificationBadgePosition = styled.div.withConfig({
  displayName: "CommentableArea__NotificationBadgePosition"
})([".hs-private.hs-private.hs-private &{position:absolute;inset-inline-end:-8px;inset-block-start:-10px;z-index:", ";}.hs-private.hs-private.hs-private &:hover{cursor:pointer;}.hs-private.hs-private.hs-private & *{box-sizing:border-box !important;}"], EARTH_LAYER);
export default function CommentableArea(_ref) {
  let {
      className,
      children,
      verticalPositioning,
      offsetOutline
    } = _ref,
    commentableAreaProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const dispatch = useDispatch();
  const collaborationSidebarState = useSelector(getCollaborationSidebarState);
  const isEmailTemplateEditor = useSelector(getIsEmailTemplate);
  const userInfo = useSelector(getAuth);
  const onUpdateCollaborationSidebarState = useCallback(updates => {
    dispatch({
      type: UPDATE_COLLABORATION_SIDEBAR_STATE,
      payload: updates
    });
  }, [dispatch]);
  if (isEmailTemplateEditor) {
    // @bend-expect trellis-tokenized
    return /*#__PURE__*/_jsx("span", {
      className: classNames(className, NAMESPACE_ENABLE),
      children: children
    });
  }

  /*
      We're wrapping these comments in this element with namespace-enable
      to enable the styled components through it's namespace requirement (see the webpack config)
    */
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx("span", {
      className: NAMESPACE_ENABLE,
      children: /*#__PURE__*/_jsx(StyledControlledCommentableArea, Object.assign({}, commentableAreaProps, {
        className: className,
        collaborationSidebarState: collaborationSidebarState,
        onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState,
        verticalPositioning: verticalPositioning,
        offsetOutline: offsetOutline,
        embeddedPopoverAnchorSelector: null,
        isCommentModeActive: collaborationSidebarState.isCommentModeActive,
        userInfo: userInfo,
        renderCommentIndicator: ({
          onClick,
          notificationBadge,
          buttonA11yProps
        }) => /*#__PURE__*/_jsxs(MouseEventsHandler, {
          type: COMMENTABLE_AREA,
          id: commentableAreaProps.moduleId,
          isSelected: false,
          onClick: e => {
            e.stopPropagation();
            onClick(e);
          },
          children: [/*#__PURE__*/_jsx(HoverableIconCircle, {
            id: "comment-indicator",
            "data-test-id": "comment-indicator",
            className: "comment-indicator",
            boxShadow: false,
            children: /*#__PURE__*/_jsx(IconCircle, Object.assign({
              icon: CommentsIcon,
              onClick: onClick,
              color: "text-core-on-fill-default",
              backgroundColor: "fill-brand-default",
              borderWidth: "0",
              blockSize: `${BADGE_SIZE}px`,
              inlineSize: `${BADGE_SIZE}px`,
              padding: "100"
            }, buttonA11yProps))
          }), /*#__PURE__*/_jsx(NotificationBadgePosition, {
            id: "comment-indicator",
            onClick: onClick,
            children: notificationBadge
          })]
        }),
        children: /*#__PURE__*/_jsx(_Fragment, {
          children: children
        })
      }))
    })
  );
}